"use client";

import { escapeIlike } from "@/lib/postgrest-filter";
import { supabase } from "@/lib/supabase";
import type { DbChat, DbChatType } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

// ── Helpers ───────────────────────────────────────────────────────────────────

async function getPersonalTeamId(): Promise<string | null> {
  const { data: { session } } = await supabase.auth.getSession();
  const user = session?.user;
  if (!user) return null;

  // 1. Prefer the user's own personal (home) team
  const { data: home } = await supabase
    .from("teams")
    .select("id")
    .eq("user_id", user.id)
    .eq("is_home", true)
    .maybeSingle();

  if (home?.id) return home.id;

  // 2. Fall back to any team the user owns
  const { data: owned } = await supabase
    .from("teams")
    .select("id")
    .eq("user_id", user.id)
    .limit(1)
    .maybeSingle();

  if (owned?.id) return owned.id;

  // 3. Fall back to any team the user is a member of
  const { data: member } = await supabase
    .from("team_members")
    .select("team_id")
    .eq("user_id", user.id)
    .limit(1)
    .maybeSingle();

  return (member as { team_id: string } | null)?.team_id ?? null;
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export type UseChatsReturn = {
  chats: DbChat[];
  loading: boolean;
  refresh: () => void;
  /** Create a real chat record in the DB. Returns the new chat id. */
  createChat: (params: {
    name: string;
    model: string;
    teamId?: string;
    agentId?: string;
    projectId?: string;
    /** Which surface is creating the chat. Defaults to "general". */
    type?: DbChatType;
    /** Initial per-chat tool settings. Same shape as profile_tools. */
    chatTools?: Array<{ tool_id: string; config: Record<string, unknown> }> | null;
  }) => Promise<string | null>;
  /** Create a project_chats record. Returns the new project chat id. */
  createProjectChat: (params: {
    name: string;
    model: string;
    agentId?: string;
    projectId: string;
    chatTools?: Array<{ tool_id: string; config: Record<string, unknown> }> | null;
  }) => Promise<string | null>;
  updateChatName: (
    chatId: string,
    name: string,
    options?: { manuallyEdited?: boolean }
  ) => Promise<boolean>;
  updateChatTools: (chatId: string, tools: Array<{ tool_id: string; config: Record<string, unknown> }>) => Promise<boolean>;
  /** Persist a per-chat model override on a personal chat. */
  updateChatModel: (chatId: string, model: string) => Promise<boolean>;
  /** Switch the persistent agent for a personal chat without creating a new conversation. */
  updateChatAgent: (chatId: string, agentId: string, agentName: string, model: string) => Promise<boolean>;
  /** Reflect a server-completed agent deactivation in the local chat list. */
  applyChatAgentDeactivation: (chatId: string) => void;
  /** Persist a per-chat model override on a project chat. */
  updateProjectChatModel: (projectChatId: string, model: string) => Promise<boolean>;
  updateProjectChatName: (projectChatId: string, name: string) => Promise<boolean>;
  deleteChat: (chatId: string) => Promise<boolean>;
  deleteProjectChat: (projectChatId: string) => Promise<boolean>;
};

// Supabase returns nested join as { agents: { name: string } | null }
// Flatten it into agent_name for convenience.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function normalise(rows: any[] | null): DbChat[] {
  if (!rows) return [];
  return rows.map(({ agents, agent_id, ...rest }) => ({
    ...rest,
    agent_id: agent_id ?? null,
    agent_name: agents?.name ?? null,
  }));
}

// ── Cursor pagination ──────────────────────────────────────────────────────────
// Keyset cursor for the (last_message_at DESC NULLS LAST, created_at DESC, id DESC)
// ordering — matches idx_chats_user_sort so each page is a single index scan.

export type ChatsCursor = {
  lastMessageAt: string | null;
  createdAt: string;
  id: string;
};

export function chatCursorFromRow(chat: DbChat): ChatsCursor {
  return {
    lastMessageAt: chat.last_message_at,
    createdAt: chat.created_at,
    id: chat.id,
  };
}

export async function fetchChatsPage(
  userId: string,
  cursor: ChatsCursor | null,
  pageSize: number,
  search?: string,
  /** Narrow to one chat surface. Filtered in the query, not after: a page of
   *  20 rows can hold no chats of the requested type at all. */
  type?: DbChatType
): Promise<DbChat[]> {
  let query = supabase
    .from("chats")
    .select("*, agents!chats_agent_id_fkey(name)")
    .eq("user_id", userId)
    // A chat currently streaming sorts first regardless of last_message_at,
    // which only bumps once the turn is persisted — without this an
    // in-progress chat sits wherever its *previous* activity left it,
    // instead of surfacing as the most-recent thing happening right now.
    // Ordering by the column itself (nulls last) rather than a computed
    // boolean, since PostgREST's .order() takes column names, not
    // expressions — ties among concurrently-streaming chats (rare) fall
    // through to the tiebreakers below in no particular guaranteed order,
    // which is fine. Note: the keyset cursor below doesn't account for
    // this extra leading sort key, so a chat starting/finishing a stream
    // exactly at a page boundary could in theory duplicate or skip across
    // "load more" pages — a rare, self-correcting cosmetic edge case, not
    // worth a 4-column cursor for.
    .order("active_stream_id", { ascending: false, nullsFirst: false })
    .order("last_message_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false })
    .order("id", { ascending: false })
    .limit(pageSize);

  const trimmedSearch = search?.trim();
  if (trimmedSearch) {
    query = query.ilike("name", `%${escapeIlike(trimmedSearch)}%`);
  }

  if (type) {
    query = query.eq("type", type);
  }

  if (cursor) {
    // Rows that sort strictly after `cursor` in the ordering above.
    query = cursor.lastMessageAt
      ? query.or(
          [
            "last_message_at.is.null",
            `last_message_at.lt.${cursor.lastMessageAt}`,
            `and(last_message_at.eq.${cursor.lastMessageAt},created_at.lt.${cursor.createdAt})`,
            `and(last_message_at.eq.${cursor.lastMessageAt},created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`,
          ].join(",")
        )
      : query.or(
          [
            `and(last_message_at.is.null,created_at.lt.${cursor.createdAt})`,
            `and(last_message_at.is.null,created_at.eq.${cursor.createdAt},id.lt.${cursor.id})`,
          ].join(",")
        );
  }

  const { data, error } = await query;

  if (error) {
    console.error("[fetchChatsPage] fetch error:", error);
    return [];
  }

  return normalise(data);
}

export function useChats(): UseChatsReturn {
  const [chats, setChats] = useState<DbChat[]>([]);
  const [loading, setLoading] = useState(true);
  const fetchChats = useCallback(async () => {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;

    if (!user) {
      setChats([]);
      setLoading(false);
      return;
    }

    const { data, error } = await supabase
      .from("chats")
      .select("*, agents!chats_agent_id_fkey(name)")
      .eq("user_id", user.id)
      // See the matching comment in fetchChatsPage above.
      .order("active_stream_id", { ascending: false, nullsFirst: false })
      .order("last_message_at", { ascending: false, nullsFirst: false })
      .order("created_at", { ascending: false });

    if (error) {
      console.error("[useChats] fetch error:", error);
      // last_message_at column may not exist in this DB — fall back without it
      if (error.message?.includes("last_message_at")) {
        const { data: fallback, error: fallbackError } = await supabase
          .from("chats")
          .select("*, agents!chats_agent_id_fkey(name)")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false });
        if (fallbackError) console.error("[useChats] fallback error:", fallbackError);
        setChats(normalise(fallback) ?? []);
        setLoading(false);
        return;
      }
    }

    setChats(normalise(data) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    const loadChats = async () => {
      await Promise.resolve();

      if (!isCancelled) {
        await fetchChats();
      }
    };

    void loadChats();

    return () => {
      isCancelled = true;
    };
  }, [fetchChats]);

  const createChat = useCallback(
    async ({
      name,
      model,
      teamId,
      agentId,
      projectId: _projectId,
      type = "general",
      chatTools,
    }: {
      name: string;
      model: string;
      teamId?: string;
      agentId?: string;
      projectId?: string;
      type?: DbChatType;
      chatTools?: Array<{ tool_id: string; config: Record<string, unknown> }> | null;
    }): Promise<string | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return null;

      // Resolve team — use provided id or fall back to personal team
      const resolvedTeamId =
        teamId ?? (await getPersonalTeamId());
      if (!resolvedTeamId) return null;

      const { data, error } = await supabase
        .from("chats")
        .insert({
          user_id: user.id,
          team_id: resolvedTeamId,
          agent_id: agentId ?? null,
          name: name.slice(0, 200),
          type,
          model,
          context_length: 4096,
          embeddings_provider: "openai",
          include_profile_context: false,
          include_team_instructions: false,
          prompt: "",
          temperature: 0.5,
          sharing: "private",
          chat_tools: chatTools ?? null,
        })
        .select("id")
        .single();

      if (error) {
        console.error("[useChats] createChat error:", error);
        return null;
      }

      // Refresh the list so the new chat appears in the sidebar
      fetchChats();

      return data.id as string;
    },
    [fetchChats]
  );

  const updateChatName = useCallback(async (
    chatId: string,
    name: string,
    { manuallyEdited = true }: { manuallyEdited?: boolean } = {}
  ) => {
    const nextName = name.trim().slice(0, 200);

    if (!nextName) {
      return false;
    }

    if (manuallyEdited) {
      setChats((current) =>
        current.map((chat) =>
          chat.id === chatId
            ? {
                ...chat,
                name: nextName,
                name_manually_edited: true,
              }
            : chat
        )
      );
    }

    let updateQuery = supabase
      .from("chats")
      .update({
        name: nextName,
        ...(manuallyEdited ? { name_manually_edited: true } : {}),
      })
      .eq("id", chatId);

    if (!manuallyEdited) {
      updateQuery = updateQuery.eq("name_manually_edited", false);
    }

    const { data, error } = await updateQuery
      .select("id, name")
      .maybeSingle();

    if (error) {
      console.error("[useChats] updateChatName error:", error);
      return false;
    }

    if (!data) {
      console.error("[useChats] updateChatName no rows updated", {
        chatId,
        nextName,
      });
      return false;
    }

    if (!manuallyEdited) {
      setChats((current) =>
        current.map((chat) =>
          chat.id === chatId ? { ...chat, name: data.name } : chat
        )
      );
    }

    return true;
  }, []);

  const updateChatTools = useCallback(async (
    chatId: string,
    tools: Array<{ tool_id: string; config: Record<string, unknown> }>
  ) => {
    const { error } = await supabase
      .from("chats")
      .update({ chat_tools: tools })
      .eq("id", chatId);
    if (error) {
      console.error("[useChats] updateChatTools error:", error);
      return false;
    }
    return true;
  }, []);

  const updateChatModel = useCallback(async (chatId: string, model: string) => {
    // Update local state immediately so the open chat reflects the new model
    // without waiting for a refetch.
    setChats((current) =>
      current.map((chat) => (chat.id === chatId ? { ...chat, model } : chat))
    );
    const { error } = await supabase
      .from("chats")
      .update({ model })
      .eq("id", chatId);
    if (error) {
      console.error("[useChats] updateChatModel error:", error);
      return false;
    }
    return true;
  }, []);

  const updateChatAgent = useCallback(async (
    chatId: string,
    agentId: string,
    agentName: string,
    model: string
  ) => {
    const { data, error } = await supabase
      .from("chats")
      // assistant_id is a legacy transition column. Keep it aligned until the
      // compatibility trigger/column is retired so switching an existing chat
      // does not compare the new agent_id with the previous assistant_id.
      .update({ agent_id: agentId, assistant_id: agentId, model })
      .eq("id", chatId)
      .select("id")
      .maybeSingle();
    if (error || !data) {
      console.error("[useChats] updateChatAgent error:", error ?? "No chat updated");
      return false;
    }
    setChats((current) =>
      current.map((chat) =>
        chat.id === chatId
          ? { ...chat, agent_id: agentId, agent_name: agentName, model }
          : chat
      )
    );
    return true;
  }, []);

  const applyChatAgentDeactivation = useCallback((chatId: string) => {
    setChats((current) =>
      current.map((chat) =>
        chat.id === chatId
          ? { ...chat, agent_id: null, agent_name: null }
          : chat
      )
    );
  }, []);

  const updateProjectChatModel = useCallback(async (projectChatId: string, model: string) => {
    const { error } = await supabase
      .from("project_chats")
      .update({ model })
      .eq("id", projectChatId);
    if (error) {
      console.error("[useChats] updateProjectChatModel error:", error);
      return false;
    }
    return true;
  }, []);

  const deleteChat = useCallback(async (chatId: string) => {
    const { error } = await supabase
      .from("chats")
      .delete()
      .eq("id", chatId);

    if (error) {
      console.error("[useChats] deleteChat error:", error);
      return false;
    }

    setChats((current) => current.filter((chat) => chat.id !== chatId));
    return true;
  }, []);

  // project_chats delete/rename mirror deleteChat/updateChatName above.
  // Search-index cleanup (delete) and invalidation (rename) both happen
  // automatically via DB triggers — see 20260825000004.
  const deleteProjectChat = useCallback(async (projectChatId: string) => {
    const { error } = await supabase
      .from("project_chats")
      .delete()
      .eq("id", projectChatId);

    if (error) {
      console.error("[useChats] deleteProjectChat error:", error);
      return false;
    }

    return true;
  }, []);

  const updateProjectChatName = useCallback(async (projectChatId: string, name: string) => {
    const nextName = name.trim().slice(0, 200);
    if (!nextName) return false;

    const { error } = await supabase
      .from("project_chats")
      .update({ name: nextName, name_manually_edited: true })
      .eq("id", projectChatId);

    if (error) {
      console.error("[useChats] updateProjectChatName error:", error);
      return false;
    }
    return true;
  }, []);

  const createProjectChat = useCallback(
    async ({
      name,
      model,
      agentId,
      projectId,
      chatTools,
    }: {
      name: string;
      model: string;
      agentId?: string;
      projectId: string;
      chatTools?: Array<{ tool_id: string; config: Record<string, unknown> }> | null;
    }): Promise<string | null> => {
      const { data: { session } } = await supabase.auth.getSession();
      const user = session?.user;
      if (!user) return null;

      const resolvedTeamId = await getPersonalTeamId();
      if (!resolvedTeamId) return null;

      const { data, error } = await supabase
        .from("project_chats")
        .insert({
          user_id: user.id,
          project_id: projectId,
          workspace_id: resolvedTeamId,
          agent_id: agentId ?? null,
          name: name.slice(0, 200),
          model,
          shared: false,
          chat_tools: chatTools ?? null,
        })
        .select("id")
        .single();

      if (error) {
        console.error("[useChats] createProjectChat error:", error);
        return null;
      }

      return data.id as string;
    },
    []
  );

  return { chats, loading, refresh: fetchChats, createChat, createProjectChat, updateChatName, updateChatTools, updateChatModel, updateChatAgent, applyChatAgentDeactivation, updateProjectChatModel, updateProjectChatName, deleteChat, deleteProjectChat };
}
